import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

interface StyledInputProps {
  $width?: string;
  $height?: string;
  $borderRadius?: string;
}

const StyledInput = styled.input<StyledInputProps>`
  ${tw`p-2 border border-border-default bg-white`}
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '44px'};
  border-radius: ${({ $borderRadius }) => $borderRadius || '0.25rem'};
`;

export interface NumericInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> {
  value: number | null;
  onValueChange: (value: number | null) => void;
  /** CSS width, e.g. '100%', '200px'. Default: '100%'. */
  width?: string;
  /** CSS height, e.g. '44px'. Default: '44px'. */
  height?: string;
  /** CSS border-radius, e.g. '0.25rem' or '8px'. Default: '0.25rem'. */
  borderRadius?: string;
  /**
   * Allow negative values (e.g. -100 for adjustments). Default: false.
   * Prefer using supportBelowZeroNumber for new code; this is kept for backwards compatibility.
   */
  allowNegative?: boolean;
  /** When true, allows numbers below 0 (i.e. negative values). Default: false. */
  supportBelowZeroNumber?: boolean;
  /** Minimum allowed numeric value (inclusive). Optional. */
  minValue?: number;
  /** Maximum allowed numeric value (inclusive). Optional. */
  maxValue?: number;
  /** When true, allows decimal numbers (小数点). Default: false. */
  allowDecimal?: boolean;
  /**
   * Custom validation function. Return false or a string error message to reject the value.
   * Return true or undefined to accept the value.
   */
  validation?: (value: number | null) => boolean | string | undefined;
}

/**
 * Reusable numeric-only input component.
 *
 * - Renders as a text input (no browser number arrows)
 * - Shows numeric keyboard on mobile via inputMode="numeric"
 * - Allows clearing the field (value becomes null)
 * - Restricts keyboard input to digits and common control keys
 * - Places the caret at the end when focused
 * - Supports decimal numbers when allowDecimal is true
 * - Supports custom validation via validation prop
 */
const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onValueChange,
  width,
  height,
  borderRadius,
  allowNegative = false,
  supportBelowZeroNumber = false,
  minValue,
  maxValue,
  allowDecimal = false,
  validation,
  ...rest
}) => {
  const negativeAllowed = allowNegative || supportBelowZeroNumber;
  const stringValue = value === null || value === undefined ? '' : value.toString();

  // Internal string state so we can temporarily show '-', '+', or empty while user is typing
  const [internalValue, setInternalValue] = React.useState(stringValue);

  // Keep internal state in sync when external value changes from outside
  React.useEffect(() => {
    setInternalValue(stringValue);
  }, [stringValue]);

  return (
    <StyledInput
      $width={width}
      $height={height}
      $borderRadius={borderRadius}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={internalValue}
      onChange={e => {
        const raw = e.target.value;
        const trimmed = raw.trim();

        // Always reflect raw input in the UI
        setInternalValue(raw);

        // Allow empty string (or just '+' / '-' / '.') so user can clear and re-enter
        if (
          trimmed === '' ||
          (negativeAllowed && (trimmed === '-' || trimmed === '+')) ||
          (allowDecimal && trimmed === '.')
        ) {
          onValueChange(null);
          return;
        }

        // If allowDecimal is true and input ends with '.', keep the intermediate state
        // Don't update the value yet - wait for user to complete the decimal input
        if (allowDecimal && trimmed.endsWith('.')) {
          // Check if it's a valid intermediate state like "12." or "-12." or "+12."
          const decimalPattern = negativeAllowed ? /^[+-]?\d+\.$/ : /^\d+\.$/;
          if (decimalPattern.test(trimmed)) {
            // Input like "12." or "-12." - keep it as intermediate state, don't call onValueChange
            return;
          }
        }

        // Parse as float if decimals are allowed, otherwise as int
        const num = allowDecimal ? parseFloat(trimmed) : parseInt(trimmed, 10);
        if (Number.isNaN(num)) {
          return;
        }

        if (!negativeAllowed && num < 0) {
          return;
        }

        if (minValue !== undefined && num < minValue) {
          return;
        }

        if (maxValue !== undefined && num > maxValue) {
          return;
        }

        // Apply custom validation if provided
        if (validation) {
          const validationResult = validation(num);
          if (validationResult === false || typeof validationResult === 'string') {
            // Validation failed, don't update the value
            return;
          }
        }

        onValueChange(num);
      }}
      onFocus={e => {
        // Move cursor to end of input when focused
        const target = e.target as HTMLInputElement;
        const length = target.value.length;
        target.setSelectionRange(length, length);
      }}
      onKeyDown={e => {
        // Allow digits
        if (/[0-9]/.test(e.key)) {
          return;
        }

        // Allow +/- when negatives are allowed
        if (negativeAllowed && (e.key === '-' || e.key === '+')) {
          return;
        }

        // Allow decimal point when decimals are allowed
        if (allowDecimal && e.key === '.') {
          // Only allow one decimal point
          const currentValue = (e.target as HTMLInputElement).value;
          if (currentValue.includes('.')) {
            // Already has a decimal point, block this one
            e.preventDefault();
            return;
          }
          // No decimal point yet, allow it
          return;
        }

        // Allow control/navigation keys
        if (
          [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
            'Enter',
            'Home',
            'End',
          ].includes(e.key) ||
          e.ctrlKey ||
          e.metaKey
        ) {
          return;
        }

        // Block everything else
        e.preventDefault();
      }}
      {...rest}
    />
  );
};

export default NumericInput;
